import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { Button } from './ui/button';
import { Car, Package, Check, MapPin, Navigation, MessageCircle, RefreshCw, X, Volume2, VolumeX, Bell, Star, Clock, Zap } from 'lucide-react';
import MapView from './MapView';
import GoogleRideTrackingMap from './GoogleRideTrackingMap';
import ChatInterface from './ChatInterface';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { getStatusLabel } from '../utils/statusLabels';

const ProviderServices = () => {
  const { user } = useAuth();
  const isTaxi = user?.provider_type === 'taxi';
  const isDelivery = user?.provider_type === 'delivery';
  const isRestaurant = ['restaurant', 'local'].includes(user?.provider_type);

  const [availableItems, setAvailableItems] = useState([]);
  const [myItems, setMyItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [loading, setLoading] = useState(true);
  const [counterOffers, setCounterOffers] = useState({});
  const [ridePins, setRidePins] = useState({});
  const [driverAvailable, setDriverAvailable] = useState(true);
  const [taxiSettings, setTaxiSettings] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem('provi_taxi_sound_enabled') !== 'false');
  const [soundUnlocked, setSoundUnlocked] = useState(false);
  const [providerLocation, setProviderLocation] = useState(null);
  const [previousPrices, setPreviousPrices] = useState({});
  const [highlightedRides, setHighlightedRides] = useState({});
  const [taxiEtas, setTaxiEtas] = useState({});
  const [hiddenRideIds, setHiddenRideIds] = useState({});
  const [pendingOfferFeedback, setPendingOfferFeedback] = useState({});
  const [expandedTaxiRideId, setExpandedTaxiRideId] = useState(null);
  const [rideGpsState, setRideGpsState] = useState('idle');
  const [rideGpsError, setRideGpsError] = useState('');
  const [toast, setToast] = useState('');
  const [driverCancelDialog, setDriverCancelDialog] = useState({
    open: false,
    ride: null,
    reason: '',
    description: '',
    evidence_url: '',
    evidence_type: ''
  });
  const [submittingDriverCancel, setSubmittingDriverCancel] = useState(false);

  const driverCancelReasons = [
    { key: 'client_no_show', label: 'Cliente no se presentó', level: 'normal' },
    { key: 'wrong_address', label: 'Dirección incorrecta', level: 'normal' },
    { key: 'inaccessible_zone', label: 'Zona inaccesible', level: 'normal' },
    { key: 'minor_mechanical_issue', label: 'Problema mecánico leve', level: 'normal' },
    { key: 'heavy_traffic', label: 'Tráfico extremo', level: 'normal' },
    { key: 'accepted_by_mistake', label: 'Acepté por error', level: 'normal' },
    { key: 'client_changed_destination_outside_app', label: 'Cliente pidió cambiar destino fuera de la app', level: 'normal' },
    { key: 'verbal_aggression', label: 'Agresión verbal', level: 'strong' },
    { key: 'physical_aggression', label: 'Agresión física', level: 'strong' },
    { key: 'threat', label: 'Amenaza', level: 'strong' },
    { key: 'dangerous_client', label: 'Cliente en estado alterado o peligroso', level: 'strong' },
    { key: 'non_payment_attempt', label: 'Intento de no pago', level: 'strong' },
    { key: 'vehicle_damage', label: 'Daño al vehículo', level: 'strong' },
    { key: 'police_situation', label: 'Situación policial', level: 'strong' },
    { key: 'medical_emergency', label: 'Emergencia médica', level: 'strong' },
    { key: 'accident', label: 'Accidente', level: 'strong' },
    { key: 'other', label: 'Otro motivo', level: 'normal' }
  ];
  const firstTaxiLoad = useRef(true);
  const audioContextRef = useRef(null);
  const hiddenRideIdsRef = useRef({});
  const activeGpsWatchRef = useRef(null);
  const activeGpsRideIdRef = useRef(null);
  const lastRideGpsPushRef = useRef(0);

  useEffect(() => {
    if (!isTaxi && !isDelivery && !isRestaurant) {
      setLoading(false);
      return undefined;
    }
    loadData();
    const interval = setInterval(loadData, 5000); // Poll every 5s
    return () => clearInterval(interval);
  }, [isTaxi, isDelivery, isRestaurant]);

  useEffect(() => {
    localStorage.setItem('provi_taxi_sound_enabled', String(soundEnabled));
  }, [soundEnabled]);

  useEffect(() => {
    hiddenRideIdsRef.current = hiddenRideIds;
  }, [hiddenRideIds]);

  useEffect(() => {
    if (!isTaxi) return;
    axios.get(`${API}/taxi/settings`, { withCredentials: true })
      .then(({ data }) => {
        setTaxiSettings(data);
        if (localStorage.getItem('provi_taxi_sound_enabled') === null) {
          setSoundEnabled(data.sound_enabled_default !== false);
        }
      })
      .catch(() => setTaxiSettings(null));
  }, [isTaxi]);

  const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;

  const getMinimumCounterOffer = () => Math.max(Number(taxiSettings?.minimum_fare || 0), 0.5);

  const getBaseOfferPrice = (ride) => {
    const minimum = getMinimumCounterOffer();
    const base = Number(ride?.offered_price || ride?.suggested_price || minimum);
    return Number((Number.isFinite(base) && base > 0 ? Math.max(base, minimum) : minimum).toFixed(2));
  };

  const getCounterOfferValue = (ride) => {
    const current = counterOffers[ride.id];
    if (current === undefined || current === null || current === '') {
      return getBaseOfferPrice(ride);
    }
    const parsed = Number(current);
    if (!Number.isFinite(parsed)) return getBaseOfferPrice(ride);
    return Number(Math.max(parsed, getMinimumCounterOffer()).toFixed(2));
  };

  const adjustCounterOffer = (ride, delta) => {
    const current = getCounterOfferValue(ride);
    const next = Math.max(getMinimumCounterOffer(), current + Number(delta || 0));
    setCounterOffers((state) => ({ ...state, [ride.id]: Number(next.toFixed(2)) }));
  };

  const handleManualCounterOffer = (ride, value) => {
    if (value === '') {
      setCounterOffers((state) => ({ ...state, [ride.id]: '' }));
      return;
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return;
    setCounterOffers((state) => ({ ...state, [ride.id]: Number(Math.max(parsed, getMinimumCounterOffer()).toFixed(2)) }));
  };

  const toCents = (value) => Math.round(Number(value || 0) * 100);

  const getClientOfferPrice = (ride) => {
    const minimum = getMinimumCounterOffer();
    const raw = Number(ride?.offered_price || ride?.suggested_price || minimum);
    return Number((Number.isFinite(raw) && raw > 0 ? Math.max(raw, minimum) : minimum).toFixed(2));
  };

  const isCounterOfferAdjusted = (ride) => toCents(getCounterOfferValue(ride)) !== toCents(getClientOfferPrice(ride));

  const haversineKm = (from, to) => {
    if (!from?.lat || !from?.lng || !to?.lat || !to?.lng) return null;
    const toRad = (degrees) => degrees * Math.PI / 180;
    const earthKm = 6371;
    const dLat = toRad(to.lat - from.lat);
    const dLng = toRad(to.lng - from.lng);
    const lat1 = toRad(from.lat);
    const lat2 = toRad(to.lat);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return earthKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const getPublicPickupLocation = (ride) => ride?.pickup_location_public || ride?.pickup_location;
  const getPublicDropoffLocation = (ride) => ride?.dropoff_location_public || ride?.dropoff_location;

  const getRideDistances = (ride) => {
    const pickup = getPublicPickupLocation(ride);
    const dropoff = getPublicDropoffLocation(ride);
    const toClient = haversineKm(providerLocation, pickup);
    const toClientDuration = toClient ? Math.max(2, Math.round(toClient * 3)) : null;
    const tripDistance = Number(ride.estimated_distance || 0) || haversineKm(pickup, dropoff);
    const duration = Number(ride.estimated_duration || 0) || (tripDistance ? Math.max(3, Math.round(tripDistance * 3)) : null);
    return { toClient, toClientDuration, tripDistance, duration };
  };

  const unlockSound = () => {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
    }
    audioContextRef.current.resume?.();
    setSoundUnlocked(true);
  };

  const playNewRequestSound = () => {
    if (!soundEnabled || !soundUnlocked) return;
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const context = audioContextRef.current || new AudioContext();
    audioContextRef.current = context;
    const volume = Math.min(Math.max(Number(taxiSettings?.sound_volume ?? 0.8), 0), 1);
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, context.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(1320, context.currentTime + 0.14);
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18 * volume, context.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.32);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.34);
  };

  const markHighlighted = (rideId, reason = 'new') => {
    setHighlightedRides((current) => ({ ...current, [rideId]: reason }));
    window.setTimeout(() => {
      setHighlightedRides((current) => {
        const next = { ...current };
        delete next[rideId];
        return next;
      });
    }, 3000);
  };

  const loadData = async () => {
    try {
      if (isTaxi) {
        const [available, mine] = await Promise.all([
          axios.get(`${API}/rides/available`, { withCredentials: true }),
          axios.get(`${API}/rides/my`, { withCredentials: true })
        ]);
        const visibleAvailable = (available.data || [])
          .filter((ride) => !hiddenRideIdsRef.current[ride.id])
          .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        setAvailableItems(visibleAvailable);
        setMyItems(mine.data);
        setPreviousPrices((current) => {
          const next = { ...current };
          const incomingIds = new Set(visibleAvailable.map((ride) => ride.id));
          visibleAvailable.forEach((ride) => {
            const price = Number(ride.offered_price || 0);
            if (!firstTaxiLoad.current && !current[ride.id]) {
              markHighlighted(ride.id, 'new');
              playNewRequestSound();
            } else if (!firstTaxiLoad.current && current[ride.id] && price > current[ride.id]) {
              markHighlighted(ride.id, 'better');
              playNewRequestSound();
            }
            next[ride.id] = price;
          });
          Object.keys(next).forEach((rideId) => {
            if (!incomingIds.has(rideId)) delete next[rideId];
          });
          firstTaxiLoad.current = false;
          return next;
        });
      } else if (isDelivery) {
        const [available, mine, foodAvailable, foodMine] = await Promise.all([
          axios.get(`${API}/delivery/available`, { withCredentials: true }),
          axios.get(`${API}/delivery/my`, { withCredentials: true }),
          axios.get(`${API}/provider/delivery/food-orders/available`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/food/orders/my`, { withCredentials: true }).catch(() => ({ data: [] }))
        ]);
        setAvailableItems([
          ...available.data.map((item) => ({ ...item, order_kind: 'delivery' })),
          ...foodAvailable.data.map((item) => ({ ...item, order_kind: 'food', service_type: 'comida' }))
        ]);
        setMyItems([
          ...mine.data.map((item) => ({ ...item, order_kind: 'delivery' })),
          ...foodMine.data.map((item) => ({ ...item, order_kind: 'food', service_type: 'comida' }))
        ]);
      } else if (isRestaurant) {
        const orders = await axios.get(`${API}/provider/restaurant/orders`, { withCredentials: true });
        setAvailableItems([]);
        setMyItems(orders.data || []);
      }
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCounterOffer = async (rideOrId) => {
    const ride = typeof rideOrId === 'object'
      ? rideOrId
      : availableItems.find((item) => item.id === rideOrId) || { id: rideOrId };
    const rideId = ride.id;
    const value = getCounterOfferValue(ride);
    if (!value || value < getMinimumCounterOffer()) {
      alert('Ingresa un precio valido.');
      return;
    }
    try {
      await axios.post(`${API}/rides/${rideId}/offers`, {
        offered_price: value,
        offer_type: 'counteroffer'
      }, { withCredentials: true });
      setCounterOffers((current) => ({ ...current, [rideId]: value }));
      loadData();
      setToast(`Contraoferta enviada por ${formatMoney(value)}.`);
      window.setTimeout(() => setToast(''), 2500);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al enviar contraoferta');
    }
  };

  const handleQuickCounterOffer = async (ride, increment) => {
    const nextPrice = Number(ride.offered_price || ride.suggested_price || 0) + Number(increment || 0);
    try {
      await axios.post(`${API}/rides/${ride.id}/offers`, {
        offered_price: Number(nextPrice.toFixed(2)),
        offer_type: increment === 0 ? 'accept_client_price' : 'counteroffer'
      }, { withCredentials: true });
      setCounterOffers((current) => ({ ...current, [ride.id]: '' }));
      setToast(increment === 0 ? 'Aceptaste el precio del cliente. Esperando confirmación.' : `Contraoferta enviada por ${formatMoney(nextPrice)}.`);
      window.setTimeout(() => setToast(''), 2500);
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al enviar contraoferta');
    }
  };

  const handleSubmitTaxiOffer = async (ride) => {
    const clientOfferPrice = getClientOfferPrice(ride);
    const counterOfferValue = getCounterOfferValue(ride);
    const adjusted = isCounterOfferAdjusted(ride);
    const price = adjusted ? counterOfferValue : clientOfferPrice;
    const offerType = adjusted ? 'counteroffer' : 'accept_client_price';

    if (!price || price < getMinimumCounterOffer()) {
      alert('Ingresa un precio valido.');
      return;
    }

    try {
      await axios.post(`${API}/rides/${ride.id}/offers`, {
        offered_price: Number(price.toFixed(2)),
        offer_type: offerType
      }, { withCredentials: true });

      setCounterOffers((current) => ({ ...current, [ride.id]: Number(price.toFixed(2)) }));
      setPendingOfferFeedback((current) => ({
        ...current,
        [ride.id]: {
          offerType,
          price: Number(price.toFixed(2)),
          createdAt: Date.now()
        }
      }));

      setToast(adjusted ? `Contraoferta enviada por ${formatMoney(price)}.` : `Carrera aceptada por ${formatMoney(price)}. Esperando al cliente.`);
      window.setTimeout(() => setToast(''), 2500);
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al enviar oferta');
    }
  };

  const handleRejectRide = async (rideId) => {
    setHiddenRideIds((current) => ({ ...current, [rideId]: true }));
    setAvailableItems((current) => current.filter((ride) => ride.id !== rideId));
    setToast('Solicitud ocultada temporalmente.');
    window.setTimeout(() => setToast(''), 2500);
    try {
      await axios.post(`${API}/rides/${rideId}/reject-for-provider`, {}, { withCredentials: true });
    } catch (error) {
      setHiddenRideIds((current) => {
        const next = { ...current };
        delete next[rideId];
        return next;
      });
      alert(error.response?.data?.detail || 'No se pudo rechazar la solicitud');
      loadData();
    }
  };

  const handleRideAction = async (rideId, action) => {
    try {
      if (action === 'start') {
        await axios.post(`${API}/rides/${rideId}/start`, { pin: ridePins[rideId] || '' }, { withCredentials: true });
      } else if (action === 'complete') {
        await axios.post(`${API}/rides/${rideId}/complete`, {}, { withCredentials: true });
      } else {
        await axios.post(`${API}/rides/action`, {
          ride_id: rideId,
          action: action
        }, { withCredentials: true });
      }
      loadData();
      if (selectedItem?.id === rideId) {
        // Refresh selected item
        const res = await axios.get(`${API}/rides/${rideId}`, { withCredentials: true });
        setSelectedItem(res.data);
      }
    } catch (error) {
      alert(error.response?.data?.detail || `Error: ${action}`);
    }
  };

  const closeDriverCancelDialog = () => {
    setDriverCancelDialog({
      open: false,
      ride: null,
      reason: '',
      description: '',
      evidence_url: '',
      evidence_type: ''
    });
  };

  const openDriverCancelDialog = (ride) => {
    setDriverCancelDialog({
      open: true,
      ride,
      reason: '',
      description: '',
      evidence_url: '',
      evidence_type: ''
    });
  };

  const updateDriverCancelDialog = (field, value) => {
    setDriverCancelDialog((current) => ({ ...current, [field]: value }));
  };

  const getDriverCancelDescriptionWordCount = () => {
    return (driverCancelDialog.description || '').trim().split(/\s+/).filter(Boolean).length;
  };

  const getSelectedDriverCancelReason = () => {
    return driverCancelReasons.find((reason) => reason.key === driverCancelDialog.reason);
  };

  const submitDriverCancelRide = async () => {
    const ride = driverCancelDialog.ride;
    if (!ride) return;

    const selectedReason = getSelectedDriverCancelReason();
    const description = (driverCancelDialog.description || '').trim();
    const evidenceUrl = (driverCancelDialog.evidence_url || '').trim();
    const evidenceType = driverCancelDialog.evidence_type || '';

    if (!driverCancelDialog.reason) {
      alert('Selecciona un motivo de cancelación.');
      return;
    }

    if (selectedReason?.level === 'strong') {
      if (!description || getDriverCancelDescriptionWordCount() < 8) {
        alert('Describe mejor la situación. Para motivos graves escribe al menos 8 palabras.');
        return;
      }

      if (!evidenceUrl || !evidenceType) {
        alert('Este motivo grave requiere evidencia real: agrega link y selecciona si es foto o audio.');
        return;
      }
    }

    if (evidenceUrl && !evidenceType) {
      alert('Selecciona si la evidencia es foto o audio.');
      return;
    }

    setSubmittingDriverCancel(true);
    try {
      await axios.post(`${API}/rides/${ride.id}/cancel`, {
        reason: driverCancelDialog.reason,
        description,
        evidence_url: evidenceUrl || null,
        evidence_type: evidenceUrl ? evidenceType : null
      }, { withCredentials: true });

      closeDriverCancelDialog();
      stopRideGpsWatch();
      loadData();
      setToast('Viaje cancelado y registrado.');
      window.setTimeout(() => setToast(''), 2500);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al cancelar viaje');
    } finally {
      setSubmittingDriverCancel(false);
    }
  };

  const handleAcceptDelivery = async (orderId) => {
    try {
      const item = availableItems.find((candidate) => candidate.id === orderId);
      const url = item?.order_kind === 'food'
        ? `${API}/provider/delivery/food-orders/${orderId}/accept`
        : `${API}/delivery/${orderId}/accept`;
      await axios.post(url, {}, { withCredentials: true });
      loadData();
    } catch (error) {
      alert('Error al aceptar delivery');
    }
  };

  const handleRestaurantOrder = async (orderId, action) => {
    try {
      if (['accept', 'reject'].includes(action)) {
        await axios.put(`${API}/provider/restaurant/orders/${orderId}/${action}`, {}, { withCredentials: true });
      } else {
        await axios.put(`${API}/provider/restaurant/orders/${orderId}/status`, { status: action }, { withCredentials: true });
      }
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al actualizar pedido');
    }
  };

  const handleDeliveryStatus = async (orderId, status, photoFile = null) => {
    try {
      const item = myItems.find((candidate) => candidate.id === orderId);
      const url = item?.order_kind === 'food'
        ? `${API}/provider/delivery/food-orders/${orderId}/status`
        : `${API}/delivery/${orderId}/status`;
      const fd = new FormData();
      fd.append('status', status);
      if (photoFile) fd.append('photo', photoFile);
      await axios.post(url, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      loadData();
    } catch (error) {
      alert('Error al actualizar estado');
    }
  };

  const buildGoogleMapsUrl = (pickup, dropoff) => {
    if (!pickup?.lat || !pickup?.lng || !dropoff?.lat || !dropoff?.lng) return null;
    return `https://www.google.com/maps/dir/?api=1&origin=${pickup.lat},${pickup.lng}&destination=${dropoff.lat},${dropoff.lng}&travelmode=driving`;
  };

  const buildAvailableRideMapsUrl = (ride) => {
    const pickup = getPublicPickupLocation(ride);
    if (!pickup?.lat || !pickup?.lng) return null;
    const originQuery = providerLocation?.lat && providerLocation?.lng ? `origin=${providerLocation.lat},${providerLocation.lng}&` : '';
    return `https://www.google.com/maps/dir/?api=1&${originQuery}destination=${pickup.lat},${pickup.lng}&travelmode=driving`;
  };

  const buildPickupMapsUrl = (pickup) => {
    if (!pickup?.lat || !pickup?.lng) return null;
    return `https://www.google.com/maps/dir/?api=1&destination=${pickup.lat},${pickup.lng}&travelmode=driving`;
  };

  const buildTaxiPhaseMapsUrl = (ride) => {
    const origin = ride.provider_location;
    const destination = ride.status === 'in_progress' ? ride.dropoff_location : ride.pickup_location;
    if (!destination?.lat || !destination?.lng) return null;
    const originQuery = origin?.lat && origin?.lng ? `origin=${origin.lat},${origin.lng}&` : '';
    return `https://www.google.com/maps/dir/?api=1&${originQuery}destination=${destination.lat},${destination.lng}&travelmode=driving`;
  };

  const handleTaxiEtaChange = useCallback((rideId, eta) => {
    setTaxiEtas((current) => {
      if (!eta) {
        if (!current[rideId]) return current;
        const next = { ...current };
        delete next[rideId];
        return next;
      }
      const previous = current[rideId];
      if (previous?.minutes === eta.minutes && previous?.distance_km === eta.distance_km && previous?.phase === eta.phase) {
        return current;
      }
      return { ...current, [rideId]: eta };
    });
  }, []);

  const getDriverEtaText = (ride) => {
    const eta = taxiEtas[ride.id];
    if (!ride.provider_location && ['accepted', 'driver_on_way', 'in_progress'].includes(ride.status)) {
      return 'Actualiza GPS para calcular ETA';
    }
    if (!eta) return 'Calculando ETA...';
    if (['accepted', 'driver_on_way'].includes(ride.status)) return `Llegas al cliente en ${eta.minutes} min`;
    if (ride.status === 'arrived') return `Ruta al destino: ${eta.minutes} min`;
    if (ride.status === 'in_progress') return `Llegas al destino en ${eta.minutes} min`;
    return `${eta.minutes} min`;
  };

  const getDriverStatusText = (status) => {
    if (['accepted', 'driver_on_way'].includes(status)) return 'En camino al cliente';
    if (status === 'arrived') return 'Cliente debe compartir PIN';
    if (status === 'in_progress') return 'Viaje en curso';
    if (status === 'completed') return 'Viaje completado';
    return getStatusLabel(status, 'taxi');
  };

  const getRideTrackingMode = (ride) => {
    if (['accepted', 'driver_on_way'].includes(ride.status)) return 'driver_to_pickup';
    if (ride.status === 'in_progress') return 'driver_to_dropoff';
    return 'pickup_to_dropoff';
  };

  const gpsNeedsHttps = () => {
    if (typeof window === 'undefined') return false;
    const host = window.location.hostname;
    const local = ['localhost', '127.0.0.1'].includes(host);
    return !window.isSecureContext && !local;
  };

  const getGpsHelpText = () => {
    if (gpsNeedsHttps()) {
      return 'GPS bloqueado por el navegador: en móviles necesitas HTTPS/dominio seguro. Con http://IP el navegador puede negar la ubicación.';
    }
    return 'Activa permisos de ubicación del navegador para actualizar tu taxi en tiempo real.';
  };

  const stopRideGpsWatch = () => {
    if (activeGpsWatchRef.current !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(activeGpsWatchRef.current);
    }
    activeGpsWatchRef.current = null;
    activeGpsRideIdRef.current = null;
    setRideGpsState('idle');
  };

  const postRideLocation = async (rideId, position, silent = false) => {
    try {
      const location = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy,
        updated_at: new Date().toISOString()
      };
      setProviderLocation(location);
      await axios.post(`${API}/rides/${rideId}/location`, {
        lat: location.lat,
        lng: location.lng,
        accuracy: location.accuracy
      }, { withCredentials: true });
      setRideGpsState('active');
      setRideGpsError('');
      if (!silent) {
        setToast('GPS en tiempo real activo.');
        window.setTimeout(() => setToast(''), 2500);
      }
      await loadData();
    } catch (error) {
      setRideGpsState('error');
      setRideGpsError(error.response?.data?.detail || 'Error al enviar la ubicación del taxi.');
    }
  };

  const startRideGpsWatch = (rideId, { silent = false } = {}) => {
    if (!navigator.geolocation) {
      setRideGpsState('error');
      setRideGpsError('GPS no disponible en este dispositivo.');
      if (!silent) alert('GPS no disponible en este dispositivo.');
      return;
    }

    if (gpsNeedsHttps()) {
      const message = getGpsHelpText();
      setRideGpsState('error');
      setRideGpsError(message);
      if (!silent) alert(message);
      return;
    }

    if (activeGpsWatchRef.current !== null && activeGpsRideIdRef.current === rideId) {
      setRideGpsState('active');
      return;
    }

    stopRideGpsWatch();
    activeGpsRideIdRef.current = rideId;
    setRideGpsState('starting');
    setRideGpsError('');

    activeGpsWatchRef.current = navigator.geolocation.watchPosition(
      async (position) => {
        const now = Date.now();
        if (now - lastRideGpsPushRef.current < 3500) return;
        lastRideGpsPushRef.current = now;
        await postRideLocation(rideId, position, true);
      },
      (error) => {
        const message = error?.code === 1
          ? 'Permiso de ubicación denegado. Activa ubicación para PROVI en el navegador.'
          : 'No se pudo obtener tu ubicación GPS. Revisa señal, permisos o HTTPS.';
        setRideGpsState('error');
        setRideGpsError(message);
        if (!silent) alert(message);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
    );
  };

  const handleUpdateRideGps = async (rideId) => {
    startRideGpsWatch(rideId, { silent: false });
  };

  const handleUpdateProviderGps = () => {
    if (!navigator.geolocation) {
      alert('GPS no disponible en este dispositivo.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setProviderLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          updated_at: new Date().toISOString()
        });
        setToast('Ubicacion GPS actualizada.');
        window.setTimeout(() => setToast(''), 2500);
      },
      () => alert('No se pudo obtener tu ubicacion GPS.'),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  };

  const handleConfirmTransfer = async (rideId) => {
    try {
      await axios.post(`${API}/rides/${rideId}/confirm-transfer`, {}, { withCredentials: true });
      loadData();
      alert('Pago por transferencia confirmado.');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al confirmar transferencia');
    }
  };

  const handleUpdateGps = async (orderId) => {
    if (!navigator.geolocation) {
      alert('GPS no disponible en este dispositivo.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const item = myItems.find((candidate) => candidate.id === orderId);
          const url = item?.order_kind === 'food'
            ? `${API}/provider/delivery/food-orders/${orderId}/location`
            : `${API}/delivery/${orderId}/location`;
          await axios.post(url, {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          }, { withCredentials: true });
          await loadData();
          alert('Ubicacion GPS actualizada.');
        } catch (error) {
          alert(error.response?.data?.detail || 'Error al actualizar GPS');
        }
      },
      () => alert('No se pudo obtener tu ubicacion GPS.'),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  };

  if (!isTaxi && !isDelivery && !isRestaurant) {
    return (
      <div className="glass-card p-8 rounded-3xl text-center">
        <p className="text-[#9CA3AF]">Este módulo está disponible solo para proveedores tipo Taxi o Delivery.</p>
      </div>
    );
  }

  const activeItem = myItems.find(i =>
    isTaxi
      ? ['accepted', 'driver_on_way', 'arrived', 'in_progress'].includes(i.status) || (i.status === 'completed' && i.payment_method === 'transfer' && i.transfer_status !== 'received')
      : ['accepted', 'delivery_assigned', 'picked_up', 'in_transit', 'on_the_way'].includes(i.status)
  );
  const activeItemIsFood = activeItem?.order_kind === 'food';
  const todayKey = new Date().toISOString().slice(0, 10);
  const completedTaxiRides = isTaxi ? myItems.filter((item) => item.status === 'completed') : [];
  const todayTaxiRides = isTaxi ? completedTaxiRides.filter((item) => (item.completed_at || item.created_at || '').slice(0, 10) === todayKey) : [];
  const taxiStats = {
    todayTrips: todayTaxiRides.length,
    todayEarnings: todayTaxiRides.reduce((sum, item) => sum + Number(item.final_price || item.offered_price || 0), 0),
    completedTrips: completedTaxiRides.length,
    rating: Number(user?.rating || user?.average_rating || 0)
  };
  const topRide = isTaxi ? availableItems[0] : null;

  useEffect(() => {
    if (!isTaxi || !activeItem || !['accepted', 'driver_on_way', 'arrived', 'in_progress'].includes(activeItem.status)) {
      stopRideGpsWatch();
      return;
    }
    startRideGpsWatch(activeItem.id, { silent: true });
  }, [isTaxi, activeItem?.id, activeItem?.status]);

  useEffect(() => () => stopRideGpsWatch(), []);

  const renderTaxiRideCard = (item, featured = false) => {
    const distances = getRideDistances(item);
    const highlight = highlightedRides[item.id];
    const previousPrice = previousPrices[item.id];
    const currentPrice = Number(item.offered_price || 0);
    const improved = highlight === 'better' && previousPrice && currentPrice > previousPrice;
    const counterOfferValue = getCounterOfferValue(item);
    const minimumCounterOffer = getMinimumCounterOffer();
    const clientOfferPrice = getClientOfferPrice(item);
    const offerAdjusted = isCounterOfferAdjusted(item);
    const pendingOfferInfo = pendingOfferFeedback[item.id];
    const expanded = expandedTaxiRideId === item.id || !!pendingOfferInfo;
    const primaryOfferLabel = offerAdjusted
      ? `Enviar ${formatMoney(counterOfferValue)}`
      : `Aceptar ${formatMoney(clientOfferPrice)}`;
    const offerButtonLabel = pendingOfferInfo ? 'Esperando cliente' : primaryOfferLabel;
    const offerButtonDisabled = Boolean(pendingOfferInfo);

    const displayName = item.client_display_name || item.client_name || 'Cliente';
    const pickupPoint = item.pickup_location_public || item.pickup_location;
    const dropoffPoint = item.dropoff_location_public || item.dropoff_location;
    const pickupArea = item.pickup_area || pickupPoint?.address || 'Zona aproximada';
    const dropoffArea = item.dropoff_area || dropoffPoint?.address || 'Zona aproximada';
    const toClientLabel = distances.toClient ? `${distances.toClient.toFixed(1)} km` : 'GPS pendiente';
    const etaLabel = distances.toClientDuration
      ? `${distances.toClientDuration} min`
      : (distances.duration ? `${Math.round(distances.duration)} min` : 'Tiempo pend.');
    const tripLabel = distances.tripDistance ? `${distances.tripDistance.toFixed(1)} km` : 'Viaje pend.';
    const payLabel = item.payment_method === 'transfer'
      ? 'Transferencia'
      : `Efectivo ${item.cash_bill === 'exact' ? 'exacto' : `· $${item.cash_bill || 'exacto'}`}`;
    const routeLabel = `${pickupArea} → ${dropoffArea}`;
    const detailTitle = expanded ? 'Ocultar detalle' : 'Ver detalle';

    const toggleDetail = (event) => {
      event?.stopPropagation?.();
      setExpandedTaxiRideId((current) => current === item.id ? null : item.id);
    };

    return (
      <div
        key={item.id}
        data-testid={`available-item-${item.id}`}
        className={`overflow-hidden rounded-2xl border bg-[#111217]/95 transition-all duration-300 ${
          highlight
            ? 'border-[#FF4FD8] shadow-[0_0_24px_rgba(255,0,127,0.22)]'
            : 'border-white/10'
        } ${featured ? 'ring-1 ring-[#FF007F]/35' : ''}`}
      >
        <button
          type="button"
          onClick={toggleDetail}
          className="w-full text-left p-3 hover:bg-white/[0.025] transition-colors"
          aria-expanded={expanded}
        >
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.35fr)_150px_115px_auto] gap-2 lg:items-center">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-[10px] uppercase tracking-[0.16em] text-[#FF9BD0] flex items-center gap-1.5">
                  <Bell className="w-3.5 h-3.5" />
                  {featured ? 'Destacada' : 'Solicitud'}
                </p>
                {improved && (
                  <span className="px-2 py-0.5 rounded-full bg-[#00D68F]/10 border border-[#00D68F]/30 text-[#8CF5C8] text-[10px] font-semibold">
                    Mejoró
                  </span>
                )}
              </div>

              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                <p className="min-w-0 max-w-full text-white font-semibold leading-tight truncate">{displayName}</p>
                <span className="text-xs text-[#D1D5DB]">{toClientLabel}</span>
                <span className="text-xs text-[#D1D5DB]">{etaLabel}</span>
                <span className="text-xs text-[#9CA3AF]">{tripLabel}</span>
              </div>

              <p className="mt-1 text-[11px] text-[#C8CBD3] line-clamp-1">{routeLabel}</p>
            </div>

            <div className="rounded-xl bg-[#1F2027] border border-white/[0.08] px-2 py-1.5 lg:text-right">
              <p className="text-[10px] text-[#A7AAB3]">Oferta</p>
              <div className="flex lg:justify-end items-end gap-1.5">
                {improved && (
                  <span className="text-xs text-[#9CA3AF] line-through">{formatMoney(previousPrice)}</span>
                )}
                <span className="text-2xl font-semibold text-white leading-none" style={{ fontFamily: 'Outfit' }}>
                  {formatMoney(item.offered_price)}
                </span>
              </div>
              <p className="text-[10px] text-[#A7AAB3] mt-0.5">Sug. {formatMoney(item.suggested_price || item.offered_price)}</p>
            </div>

            <div className="rounded-xl bg-[#1F2027] border border-white/[0.08] px-2 py-1.5 lg:text-right min-w-0">
              <p className="text-[10px] text-[#A7AAB3]">Pago</p>
              <p className="text-xs text-white font-medium leading-tight truncate">{payLabel}</p>
            </div>

            <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-1.5 lg:flex lg:flex-nowrap lg:justify-end">
              <Button
                data-testid={`submit-offer-${item.id}`}
                onClick={(event) => { event.stopPropagation(); handleSubmitTaxiOffer(item); }}
                disabled={offerButtonDisabled}
                className={`${pendingOfferInfo ? 'bg-cyan-500/15 border border-cyan-400/30 text-cyan-100 hover:bg-cyan-500/15' : 'gradient-button shadow-[0_0_16px_rgba(255,0,127,0.22)]'} rounded-xl min-h-[44px] px-3 text-xs font-semibold whitespace-normal leading-tight disabled:opacity-100`}
              >
                <Check className="w-3.5 h-3.5 mr-1" />
                {offerButtonLabel}
              </Button>
              <Button
                type="button"
                onClick={toggleDetail}
                className={`rounded-xl min-h-[44px] px-2.5 text-xs whitespace-nowrap ${
                  expanded
                    ? 'bg-[#00D68F]/12 border border-[#00D68F]/30 text-[#9AF4C8] hover:bg-[#00D68F]/18'
                    : 'bg-white/5 border border-white/10 text-white hover:bg-white/10'
                }`}
              >
                {detailTitle}
              </Button>
              <Button
                onClick={(event) => { event.stopPropagation(); handleRejectRide(item.id); }}
                className="bg-red-500/15 border border-red-500/30 text-red-200 hover:bg-red-500/25 rounded-xl min-h-[44px] px-2.5 text-xs whitespace-nowrap"
              >
                <X className="w-3.5 h-3.5 mr-1" />
                Rechazar
              </Button>
            </div>
          </div>
        </button>

        {expanded && (
          <div className="border-t border-white/10 bg-[#0C0F16] p-3 space-y-3">
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(320px,1fr)_320px] gap-3 items-stretch">
              <div className="rounded-2xl overflow-hidden border border-white/10 bg-black/20 min-h-[220px]">
                <GoogleRideTrackingMap
                  pickup={pickupPoint}
                  dropoff={dropoffPoint}
                  providerLocation={providerLocation}
                  rideStatus={item.status}
                  mode="request"
                  height="260px"
                  viewer="provider"
                />
              </div>

              <div className="flex flex-col gap-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-2">
                  <div className="rounded-2xl border border-white/[0.08] bg-[#20212A] px-3 py-2 min-w-0">
                    <p className="text-[10px] text-[#A7AAB3] flex items-center gap-1.5">
                      <span className="w-5 h-5 rounded-full bg-[#00F0FF]/15 text-[#00F0FF] flex items-center justify-center text-[10px] font-bold">A</span>
                      Recogida aproximada
                    </p>
                    <p className="text-sm font-semibold text-white leading-tight line-clamp-2 mt-1">{pickupArea}</p>
                  </div>

                  <div className="rounded-2xl border border-white/[0.08] bg-[#20212A] px-3 py-2 min-w-0">
                    <p className="text-[10px] text-[#A7AAB3] flex items-center gap-1.5">
                      <span className="w-5 h-5 rounded-full bg-[#FF007F]/15 text-[#FF9BD0] flex items-center justify-center text-[10px] font-bold">B</span>
                      Destino
                    </p>
                    <p className="text-sm font-semibold text-white leading-tight line-clamp-2 mt-1">{dropoffArea}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-1.5">
                  <div className="bg-white/5 rounded-lg px-2 py-1.5 border border-white/10">
                    <p className="text-[10px] text-[#9CA3AF]">Cliente</p>
                    <p className="text-xs font-semibold text-white">{toClientLabel}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg px-2 py-1.5 border border-white/10">
                    <p className="text-[10px] text-[#9CA3AF]">Viaje</p>
                    <p className="text-xs font-semibold text-white">{tripLabel}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg px-2 py-1.5 border border-white/10">
                    <p className="text-[10px] text-[#9CA3AF]">Tiempo</p>
                    <p className="text-xs font-semibold text-white">{etaLabel}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-[#2E7D5B]/80 bg-[#142A24]/90 p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.16em] text-[#66F0B4]">Tu contraoferta</p>
                  <p className="text-[10px] text-[#C9D5CF]">Mínimo: {formatMoney(minimumCounterOffer)}</p>
                </div>
                {counterOfferValue < getBaseOfferPrice(item) && (
                  <span className="text-[10px] text-yellow-200 bg-yellow-500/10 border border-yellow-500/20 rounded-full px-2 py-0.5">
                    Menor
                  </span>
                )}
              </div>

              <div className="mt-2 grid grid-cols-[70px_minmax(0,1fr)_70px] gap-2 items-center">
                <Button
                  type="button"
                  onClick={() => adjustCounterOffer(item, -0.25)}
                  disabled={counterOfferValue <= minimumCounterOffer}
                  className="bg-green-900/70 border border-green-700/60 text-green-100 hover:bg-green-800 rounded-2xl min-h-[44px] px-2 disabled:opacity-50 text-xs font-semibold"
                >
                  -$0.25
                </Button>

                <div className="min-w-0 rounded-2xl bg-[#101815] border border-white/[0.10] px-2 py-2 text-center">
                  <p className="whitespace-nowrap text-3xl sm:text-4xl font-semibold text-white leading-none" style={{ fontFamily: 'Outfit' }}>
                    {formatMoney(counterOfferValue)}
                  </p>
                </div>

                <Button
                  type="button"
                  onClick={() => adjustCounterOffer(item, 0.25)}
                  className="bg-green-500/25 border border-green-400/40 text-green-100 hover:bg-green-500/35 rounded-2xl min-h-[44px] px-2 text-xs font-semibold"
                >
                  +$0.25
                </Button>
              </div>

              <details className="mt-1">
                <summary className="cursor-pointer text-[10px] text-[#A7AAB3] hover:text-white">Otro monto</summary>
                <Input
                  type="number"
                  min={minimumCounterOffer}
                  step="0.25"
                  inputMode="decimal"
                  placeholder={formatMoney(counterOfferValue)}
                  value={counterOffers[item.id] ?? ''}
                  onChange={(event) => handleManualCounterOffer(item, event.target.value)}
                  className="mt-1 bg-white/5 border-white/10 text-white rounded-xl max-w-32 min-h-[32px] text-sm"
                />
              </details>
            </div>

            {!providerLocation && (
              <div className="rounded-2xl bg-[#2C2612] border border-yellow-500/25 px-3 py-2 flex items-center justify-between gap-2">
                <p className="text-[11px] text-yellow-200">GPS pendiente para calcular distancia al cliente.</p>
                <Button onClick={handleUpdateProviderGps} className="bg-yellow-500/20 border border-yellow-500/30 text-yellow-100 hover:bg-yellow-500/30 rounded-xl min-h-[32px] px-2.5 text-xs">
                  <Navigation className="w-3.5 h-3.5 mr-1" />
                  GPS
                </Button>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
              <Button
                onClick={() => handleRejectRide(item.id)}
                className="bg-red-500/15 border border-red-500/35 text-red-100 hover:bg-red-500/25 rounded-2xl min-h-[44px] px-4 text-base font-semibold"
              >
                <X className="w-4 h-4 mr-2" />
                Rechazar
              </Button>

              <Button
                data-testid={`expanded-submit-offer-${item.id}`}
                onClick={() => handleSubmitTaxiOffer(item)}
                disabled={offerButtonDisabled}
                className={`${pendingOfferInfo ? 'bg-cyan-500/15 border border-cyan-400/30 text-cyan-100 hover:bg-cyan-500/15' : 'gradient-button shadow-[0_0_20px_rgba(255,0,127,0.24)]'} rounded-2xl min-h-[44px] px-4 text-base font-semibold whitespace-normal leading-tight disabled:opacity-100`}
              >
                <Check className="w-4 h-4 mr-2" />
                {offerButtonLabel}
              </Button>

              <Button
                type="button"
                onClick={() => setExpandedTaxiRideId(null)}
                className="bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-2xl min-h-[44px] px-4 text-sm"
              >
                Cerrar
              </Button>
            </div>

            {pendingOfferInfo && (
              <div className="rounded-2xl border border-cyan-400/25 bg-cyan-500/10 p-2.5 flex items-center gap-2">
                <div className="w-7 h-7 rounded-full border-2 border-cyan-300/30 border-t-cyan-300 animate-spin flex-shrink-0" />
                <div>
                  <p className="text-white text-sm font-semibold">Esperando cliente</p>
                  <p className="text-xs text-[#D1D5DB] line-clamp-2">
                    {pendingOfferInfo.offerType === 'accept_client_price'
                      ? `Aceptaste por ${formatMoney(pendingOfferInfo.price)}. Se asigna cuando el cliente confirme.`
                      : `Contraoferta por ${formatMoney(pendingOfferInfo.price)}. El cliente debe aceptarla.`}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (isTaxi) {
    if (activeItem) {
      const activeMapLocation = providerLocation || activeItem.provider_location;
      const ridePhaseLabel = ['accepted', 'driver_on_way'].includes(activeItem.status)
        ? 'Ruta al cliente'
        : activeItem.status === 'arrived'
          ? 'Cliente en punto de recogida'
          : activeItem.status === 'in_progress'
            ? 'Ruta al destino'
            : 'Viaje activo';
      const gpsSecureBlocked = gpsNeedsHttps();

      return (
        <div className="space-y-4" data-testid="provider-taxi-active-page">
          {toast && (
            <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 bg-[#10131d] border border-[#FF007F]/40 text-white px-4 py-3 rounded-2xl shadow-2xl">
              {toast}
            </div>
          )}

          <div className="glass-card rounded-3xl border border-[#00F0FF]/25 overflow-hidden bg-[#080a12]/92">
            <div className="p-4 sm:p-5 border-b border-white/10">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-[0.22em] text-[#00F0FF]">Viaje activo</p>
                  <h2 className="text-3xl sm:text-4xl font-semibold text-white mt-1 leading-tight" style={{ fontFamily: 'Outfit' }}>
                    {activeItem.client_name || 'Cliente'}
                  </h2>
                  <p className="text-sm text-[#9CA3AF] mt-1">Estado: {getDriverStatusText(activeItem.status)}</p>
                  <p className="text-sm text-[#00F0FF] flex items-center gap-2 mt-2">
                    <Clock className="w-4 h-4" />
                    {getDriverEtaText({ ...activeItem, provider_location: activeMapLocation })}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button data-testid="active-chat-button" onClick={() => { setSelectedItem(activeItem); setShowChat(true); }} className="bg-white/5 border border-white/10 text-white rounded-2xl min-h-[44px]">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Chat
                  </Button>
                  <Button
                    onClick={() => handleUpdateRideGps(activeItem.id)}
                    className={`${rideGpsState === 'active' ? 'bg-[#00D68F]/15 border-[#00D68F]/35 text-[#9AF4C8]' : 'gradient-button'} rounded-2xl min-h-[44px]`}
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    {rideGpsState === 'active' ? 'GPS activo' : rideGpsState === 'starting' ? 'Activando GPS...' : 'Activar GPS en vivo'}
                  </Button>
                  {['accepted', 'driver_on_way', 'arrived', 'in_progress'].includes(activeItem.status) && (
                    <Button
                      onClick={() => openDriverCancelDialog(activeItem)}
                      className="bg-red-500/15 border border-red-500/35 text-red-100 hover:bg-red-500/25 rounded-2xl min-h-[44px]"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancelar viaje
                    </Button>
                  )}
                </div>
              </div>

              {(rideGpsError || gpsSecureBlocked) && (
                <div className="mt-4 rounded-2xl bg-yellow-500/10 border border-yellow-500/25 px-3 py-2 text-sm text-yellow-100">
                  {rideGpsError || getGpsHelpText()}
                </div>
              )}
            </div>

            <div className="p-4 sm:p-5 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-[#9CA3AF]">{ridePhaseLabel}</p>
                  <p className="text-sm text-[#D1D5DB] mt-1">
                    {activeItem.status === 'in_progress'
                      ? `${activeItem.pickup_location?.address || 'Origen'} → ${activeItem.dropoff_location?.address || 'Destino'}`
                      : `Conduce hacia ${activeItem.pickup_location?.address || 'el cliente'}`}
                  </p>
                </div>
                {rideGpsState === 'active' && (
                  <span className="px-3 py-1 rounded-full bg-[#00D68F]/10 border border-[#00D68F]/30 text-[#9AF4C8] text-xs font-semibold">
                    Ubicación enviándose en tiempo real
                  </span>
                )}
              </div>

              <div className="rounded-3xl overflow-hidden border border-white/10 bg-black/20">
                <GoogleRideTrackingMap
                  pickup={activeItem.pickup_location}
                  dropoff={activeItem.dropoff_location}
                  providerLocation={activeMapLocation}
                  rideStatus={activeItem.status}
                  mode={getRideTrackingMode(activeItem)}
                  height="clamp(330px, 58vh, 640px)"
                  viewer="provider"
                  onEtaChange={(eta) => handleTaxiEtaChange(activeItem.id, eta)}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
                <div className="rounded-2xl bg-white/5 border border-white/10 p-3">
                  <p className="text-xs text-[#9CA3AF]">Recogida</p>
                  <p className="text-sm font-semibold text-white line-clamp-2">{activeItem.pickup_location?.address || 'Pendiente'}</p>
                </div>
                <div className="rounded-2xl bg-white/5 border border-white/10 p-3">
                  <p className="text-xs text-[#9CA3AF]">Destino</p>
                  <p className="text-sm font-semibold text-white line-clamp-2">{activeItem.dropoff_location?.address || 'Pendiente'}</p>
                </div>
                <div className="rounded-2xl bg-white/5 border border-white/10 p-3">
                  <p className="text-xs text-[#9CA3AF]">Precio final</p>
                  <p className="text-xl font-semibold text-white">{formatMoney(activeItem.final_price || activeItem.offered_price)}</p>
                </div>
                <div className="rounded-2xl bg-white/5 border border-white/10 p-3">
                  <p className="text-xs text-[#9CA3AF]">Pago</p>
                  <p className="text-sm font-semibold text-white">
                    {activeItem.payment_method === 'transfer' ? 'Transferencia' : 'Efectivo'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                {['accepted', 'driver_on_way'].includes(activeItem.status) && (
                  <Button data-testid="ride-arrived" onClick={() => handleRideAction(activeItem.id, 'arrived')} className="gradient-button rounded-2xl min-h-[52px] text-base font-semibold">
                    He llegado
                  </Button>
                )}

                {activeItem.status === 'arrived' && (
                  <>
                    <div>
                      <Label className="text-xs text-[#9CA3AF]">PIN del cliente</Label>
                      <Input
                        value={ridePins[activeItem.id] || ''}
                        onChange={(e) => setRidePins((current) => ({ ...current, [activeItem.id]: e.target.value }))}
                        placeholder="1234"
                        maxLength={4}
                        className="bg-white/5 border-white/10 text-white rounded-2xl min-h-[52px]"
                      />
                    </div>
                    <Button data-testid="ride-start" onClick={() => handleRideAction(activeItem.id, 'start')} className="gradient-button rounded-2xl min-h-[52px] text-base font-semibold">
                      Iniciar viaje
                    </Button>
                  </>
                )}

                {activeItem.status === 'in_progress' && (
                  <Button data-testid="ride-complete" onClick={() => handleRideAction(activeItem.id, 'complete')} className="gradient-button rounded-2xl min-h-[52px] text-base font-semibold">
                    Finalizar viaje
                  </Button>
                )}

                {activeItem.payment_method === 'transfer' && activeItem.status === 'completed' && activeItem.transfer_status !== 'received' && (
                  <Button onClick={() => handleConfirmTransfer(activeItem.id)} className="gradient-button rounded-2xl min-h-[52px] text-base font-semibold">
                    Confirmar transferencia
                  </Button>
                )}

                {buildTaxiPhaseMapsUrl({ ...activeItem, provider_location: activeMapLocation }) && (
                  <a href={buildTaxiPhaseMapsUrl({ ...activeItem, provider_location: activeMapLocation })} target="_blank" rel="noreferrer" className="block">
                    <Button className="w-full bg-white/5 border border-white/10 text-white rounded-2xl min-h-[52px]">
                      Abrir en Google Maps
                    </Button>
                  </a>
                )}
              </div>
            </div>
          </div>

          <Dialog open={driverCancelDialog.open} onOpenChange={(open) => !open && closeDriverCancelDialog()}>
            <DialogContent className="bg-[#0B0C10] border-white/10 text-white max-w-lg rounded-3xl">
              <DialogHeader>
                <DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>
                  Cancelar viaje como taxista
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <div className="rounded-2xl bg-red-500/10 border border-red-500/25 p-3">
                  <p className="text-sm text-red-100 font-medium">Esta cancelación quedará registrada.</p>
                  <p className="text-xs text-[#D1D5DB] mt-1">
                    Los motivos graves requieren evidencia real y serán revisados por administración.
                  </p>
                </div>

                <div>
                  <label className="text-sm text-[#D1D5DB] block mb-2">Motivo</label>
                  <select
                    value={driverCancelDialog.reason}
                    onChange={(event) => updateDriverCancelDialog('reason', event.target.value)}
                    className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-3 outline-none"
                  >
                    <option value="" className="bg-[#0B0C10]">Selecciona un motivo</option>
                    {driverCancelReasons.map((reason) => (
                      <option key={reason.key} value={reason.key} className="bg-[#0B0C10]">
                        {reason.label}{reason.level === 'strong' ? ' · grave' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {getSelectedDriverCancelReason()?.level === 'strong' && (
                  <div className="rounded-2xl bg-yellow-500/10 border border-yellow-500/25 p-3">
                    <p className="text-sm text-yellow-100 font-medium">Motivo grave</p>
                    <p className="text-xs text-[#D1D5DB] mt-1">
                      Este motivo exige descripción clara y evidencia por foto o audio. Por ahora pega el enlace de la evidencia.
                    </p>
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <label className="text-sm text-[#D1D5DB]">Descripción</label>
                    {getSelectedDriverCancelReason()?.level === 'strong' && (
                      <span className={`text-xs ${getDriverCancelDescriptionWordCount() >= 8 ? 'text-green-300' : 'text-yellow-300'}`}>
                        {getDriverCancelDescriptionWordCount()}/8 palabras
                      </span>
                    )}
                  </div>
                  <textarea
                    value={driverCancelDialog.description}
                    onChange={(event) => updateDriverCancelDialog('description', event.target.value)}
                    placeholder={getSelectedDriverCancelReason()?.level === 'strong'
                      ? 'Describe lo ocurrido con detalle. Mínimo 8 palabras.'
                      : 'Describe brevemente qué ocurrió.'}
                    rows={5}
                    className="w-full bg-white/5 border border-white/10 text-white placeholder:text-[#9CA3AF] rounded-xl px-3 py-3 outline-none resize-none"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-[130px_1fr] gap-2">
                  <select
                    value={driverCancelDialog.evidence_type}
                    onChange={(event) => updateDriverCancelDialog('evidence_type', event.target.value)}
                    className="bg-white/5 border border-white/10 text-white rounded-xl px-3 py-3 outline-none"
                  >
                    <option value="" className="bg-[#0B0C10]">Evidencia</option>
                    <option value="photo" className="bg-[#0B0C10]">Foto</option>
                    <option value="audio" className="bg-[#0B0C10]">Audio</option>
                  </select>
                  <Input
                    value={driverCancelDialog.evidence_url}
                    onChange={(event) => updateDriverCancelDialog('evidence_url', event.target.value)}
                    placeholder="Link de evidencia si aplica"
                    className="bg-white/5 border-white/10 text-white placeholder:text-[#9CA3AF] rounded-xl"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-2 justify-end">
                  <Button
                    type="button"
                    onClick={closeDriverCancelDialog}
                    disabled={submittingDriverCancel}
                    className="bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-xl"
                  >
                    Volver
                  </Button>
                  <Button
                    type="button"
                    onClick={submitDriverCancelRide}
                    disabled={submittingDriverCancel}
                    className="bg-gradient-to-r from-red-500 to-[#FF007F] hover:opacity-90 text-white rounded-xl"
                  >
                    {submittingDriverCancel ? 'Cancelando...' : 'Confirmar cancelación'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={showChat} onOpenChange={setShowChat}>
            <DialogContent className="bg-[#0B0C10] border-white/10 text-white max-w-lg rounded-3xl p-0">
              <DialogHeader className="p-4 pb-0">
                <DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>
                  Chat con {selectedItem?.client_name}
                </DialogTitle>
              </DialogHeader>
              {selectedItem && (
                <div className="p-4">
                  <ChatInterface orderId={selectedItem.id} orderType="ride" otherPartyName={selectedItem.client_name} height="450px" />
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      );
    }

    return (
      <div className="space-y-5" data-testid="provider-taxi-driver-mode">
        {toast && (
          <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 bg-[#10131d] border border-[#FF007F]/40 text-white px-4 py-3 rounded-2xl shadow-2xl">
            {toast}
          </div>
        )}

        <div className="glass-card p-5 sm:p-6 rounded-3xl bg-[#080a12]/80">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#9CA3AF]">Modo conductor</p>
              <h2 className="text-3xl sm:text-4xl font-semibold text-white mt-2" style={{ fontFamily: 'Outfit' }}>
                {driverAvailable ? 'Disponible para viajes' : 'No disponible'}
              </h2>
              <p className="text-[#D1D5DB] mt-1">{user?.name} · {user?.approved ? 'Verificado' : 'Pendiente'}</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={driverAvailable}
              onClick={() => setDriverAvailable((current) => !current)}
              className={`group relative flex min-h-[72px] w-full items-center justify-between rounded-3xl px-5 text-left transition-all duration-300 sm:w-[360px] ${
                driverAvailable
                  ? 'bg-gradient-to-r from-[#FF007F] to-[#8A2BE2] text-white shadow-[0_18px_50px_rgba(255,0,127,0.24)]'
                  : 'border border-white/15 bg-white/5 text-white hover:bg-white/10'
              }`}
            >
              <span className="flex items-center gap-3">
                <span className={`grid h-11 w-11 place-items-center rounded-2xl ${driverAvailable ? 'bg-white/20' : 'bg-white/10'}`}>
                  <Zap className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-base font-semibold">
                    {driverAvailable ? 'Estoy disponible' : 'No estoy disponible'}
                  </span>
                  <span className={`block text-sm ${driverAvailable ? 'text-white/80' : 'text-[#9CA3AF]'}`}>
                    {driverAvailable ? 'Recibiendo viajes' : 'Pausado'}
                  </span>
                </span>
              </span>
              <span className={`relative h-8 w-14 rounded-full border transition-colors duration-300 ${driverAvailable ? 'border-white/30 bg-white/25' : 'border-white/15 bg-black/25'}`}>
                <span className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow-lg transition-transform duration-300 ${driverAvailable ? 'translate-x-7' : 'translate-x-1'}`} />
              </span>
            </button>
          </div>

          <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              ['Viajes de hoy', taxiStats.todayTrips],
              ['Ganancia hoy', formatMoney(taxiStats.todayEarnings)],
              ['Completados', taxiStats.completedTrips],
              ['Calificacion', taxiStats.rating ? taxiStats.rating.toFixed(1) : '0.0']
            ].map(([label, value]) => (
              <div key={label} className="bg-white/5 border border-white/10 rounded-2xl p-4">
                <p className="text-xs text-[#9CA3AF]">{label}</p>
                <p className="text-2xl font-semibold text-white mt-1">{value}</p>
              </div>
            ))}
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Button onClick={handleUpdateProviderGps} className="bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-2xl min-h-[48px]">
              <Navigation className="w-4 h-4 mr-2" />
              Actualizar mi ubicacion
            </Button>
            <Button onClick={() => { unlockSound(); setSoundEnabled((current) => !current); }} className="bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-2xl min-h-[48px]">
              {soundEnabled ? <Volume2 className="w-4 h-4 mr-2" /> : <VolumeX className="w-4 h-4 mr-2" />}
              {soundEnabled ? 'Sonido activo' : 'Sonido apagado'}
            </Button>
            {soundEnabled && !soundUnlocked && (
              <Button onClick={unlockSound} className="bg-[#FF007F]/15 border border-[#FF007F]/30 text-[#FF9BD0] hover:bg-[#FF007F]/25 rounded-2xl min-h-[48px]">
                Activar sonido
              </Button>
            )}
          </div>
        </div>

        {activeItem && (
          <div className="glass-card p-5 sm:p-6 rounded-3xl border border-[#00F0FF]/30" data-testid="active-service">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-[#00F0FF]">Viaje activo</p>
                <h3 className="text-2xl font-semibold text-white mt-1" style={{ fontFamily: 'Outfit' }}>
                  {activeItem.client_name || 'Cliente'}
                </h3>
                <p className="text-sm text-[#9CA3AF]">Estado: {getDriverStatusText(activeItem.status)}</p>
                <p className="text-sm text-[#00F0FF] flex items-center gap-2 mt-2">
                  <Clock className="w-4 h-4" />
                  {getDriverEtaText(activeItem)}
                </p>
              </div>
              <Button data-testid="active-chat-button" onClick={() => { setSelectedItem(activeItem); setShowChat(true); }} className="bg-white/5 border border-white/10 text-white rounded-2xl">
                <MessageCircle className="w-4 h-4 mr-2" />
                Chat
              </Button>
            </div>

            <GoogleRideTrackingMap
              pickup={activeItem.pickup_location}
              dropoff={activeItem.dropoff_location}
              providerLocation={activeItem.provider_location}
              rideStatus={activeItem.status}
              mode={getRideTrackingMode(activeItem)}
              height="280px"
              viewer="provider"
              onEtaChange={(eta) => handleTaxiEtaChange(activeItem.id, eta)}
            />

            <div className="flex flex-wrap gap-2 mt-4">
              <Button onClick={() => handleUpdateRideGps(activeItem.id)} className="bg-white/5 border border-white/10 text-white rounded-2xl min-h-[48px]">
                <Navigation className="w-4 h-4 mr-2" />
                Actualizar GPS
              </Button>
              {['accepted', 'driver_on_way'].includes(activeItem.status) && buildTaxiPhaseMapsUrl(activeItem) && (
                <a href={buildTaxiPhaseMapsUrl(activeItem)} target="_blank" rel="noreferrer">
                  <Button className="bg-white/5 border border-white/10 text-white rounded-2xl min-h-[48px]">
                    Ruta al cliente
                  </Button>
                </a>
              )}
              {['arrived', 'in_progress'].includes(activeItem.status) && buildTaxiPhaseMapsUrl(activeItem) && (
                <a href={buildTaxiPhaseMapsUrl(activeItem)} target="_blank" rel="noreferrer">
                  <Button className="bg-white/5 border border-white/10 text-white rounded-2xl min-h-[48px]">
                    Ruta al destino
                  </Button>
                </a>
              )}
              {['accepted', 'driver_on_way'].includes(activeItem.status) && (
                <Button data-testid="ride-arrived" onClick={() => handleRideAction(activeItem.id, 'arrived')} className="gradient-button rounded-2xl min-h-[48px]">
                  He llegado
                </Button>
              )}
              {activeItem.status === 'arrived' && (
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <Label className="text-xs text-[#9CA3AF]">PIN</Label>
                    <Input
                      value={ridePins[activeItem.id] || ''}
                      onChange={(e) => setRidePins((current) => ({ ...current, [activeItem.id]: e.target.value }))}
                      placeholder="1234"
                      maxLength={4}
                      className="bg-white/5 border-white/10 text-white rounded-2xl w-28 min-h-[48px]"
                    />
                  </div>
                  <Button data-testid="ride-start" onClick={() => handleRideAction(activeItem.id, 'start')} className="gradient-button rounded-2xl min-h-[48px]">
                    Iniciar viaje
                  </Button>
                </div>
              )}
              {activeItem.status === 'in_progress' && (
                <Button data-testid="ride-complete" onClick={() => handleRideAction(activeItem.id, 'complete')} className="gradient-button rounded-2xl min-h-[48px]">
                  Finalizar viaje
                </Button>
              )}
              {activeItem.payment_method === 'transfer' && activeItem.status === 'completed' && activeItem.transfer_status !== 'received' && (
                <Button onClick={() => handleConfirmTransfer(activeItem.id)} className="gradient-button rounded-2xl min-h-[48px]">
                  Confirmar transferencia
                </Button>
              )}
            </div>
          </div>
        )}

        <div className="glass-card p-5 sm:p-6 rounded-3xl">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>
                Nuevas solicitudes de viaje
              </h3>
              <p className="text-sm text-[#9CA3AF]">Ordenadas de mas nueva a mas antigua.</p>
            </div>
            <Button onClick={loadData} size="sm" className="bg-white/5 border border-white/10 text-white rounded-xl">
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
          </div>

          {loading ? (
            <p className="text-center py-6 text-[#9CA3AF]">Cargando solicitudes...</p>
          ) : !driverAvailable ? (
            <p className="text-center py-6 text-[#9CA3AF]">Estas pausado. Puedes conectarte cuando estes listo.</p>
          ) : availableItems.length === 0 ? (
            <p className="text-center py-6 text-[#9CA3AF]">No hay viajes disponibles en este momento.</p>
          ) : (
            <div className="space-y-4">
              {topRide && renderTaxiRideCard(topRide, true)}
              {availableItems.slice(1).map((item) => renderTaxiRideCard(item))}
            </div>
          )}
        </div>

        <Dialog open={showChat} onOpenChange={setShowChat}>
          <DialogContent className="bg-[#0B0C10] border-white/10 text-white max-w-lg rounded-3xl p-0">
            <DialogHeader className="p-4 pb-0">
              <DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>
                Chat con {selectedItem?.client_name}
              </DialogTitle>
            </DialogHeader>
            {selectedItem && (
              <div className="p-4">
                <ChatInterface orderId={selectedItem.id} orderType="ride" otherPartyName={selectedItem.client_name} height="450px" />
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  if (isRestaurant) {
    return (
      <div className="space-y-6" data-testid="provider-restaurant-services">
        <div className="glass-card p-6 rounded-3xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-white" style={{ fontFamily: 'Outfit' }}>
              Pedidos del restaurante
            </h3>
            <Button onClick={loadData} size="sm" className="bg-white/5 border border-white/10 text-white rounded-lg">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {loading ? (
            <p className="text-center py-6 text-[#9CA3AF]">Cargando...</p>
          ) : myItems.length === 0 ? (
            <p className="text-center py-6 text-[#9CA3AF]">No hay pedidos de comida para tu restaurante.</p>
          ) : (
            <div className="space-y-3">
              {myItems.map((item) => (
                <div key={item.id} className="bg-white/5 p-4 rounded-2xl border border-white/10">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-[220px]">
                      <p className="text-white font-medium">{item.client_name}</p>
                      <p className="text-xs text-[#9CA3AF] mt-1">{item.dropoff_location?.address || 'Direccion de entrega pendiente'}</p>
                      <p className="text-xs text-[#9CA3AF] mt-1">{item.items?.length || 0} productos - ${Number(item.total_price || item.total || 0).toFixed(2)}</p>
                      {item.notes && <p className="text-xs text-[#D1D5DB] mt-2">Notas: {item.notes}</p>}
                    </div>
                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 rounded-lg text-xs">
                      {getStatusLabel(item.status, 'food')}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {item.status === 'pending_restaurant' && (
                      <>
                        <Button onClick={() => handleRestaurantOrder(item.id, 'accept')} className="gradient-button rounded-xl">Aceptar</Button>
                        <Button onClick={() => handleRestaurantOrder(item.id, 'reject')} className="bg-red-500/20 text-red-300 border border-red-500/30 hover:bg-red-500/30 rounded-xl">Rechazar</Button>
                      </>
                    )}
                    {['accepted_by_restaurant', 'preparing'].includes(item.status) && (
                      <Button onClick={() => handleRestaurantOrder(item.id, item.status === 'preparing' ? 'ready_for_pickup' : 'preparing')} className="gradient-button rounded-xl">
                        {item.status === 'preparing' ? 'Marcar listo' : 'Preparar'}
                      </Button>
                    )}
                    {item.status === 'ready_for_pickup' && (
                      <Button onClick={() => handleRestaurantOrder(item.id, 'waiting_delivery')} className="gradient-button rounded-xl">
                        Esperar delivery
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="provider-services">
      {/* Active item with map */}
      {activeItem && (
        <div className="glass-card p-6 rounded-3xl" data-testid="active-service">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="text-lg font-medium text-white flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                {isTaxi ? <Car className="w-5 h-5 text-[#FF007F]" /> : <Package className="w-5 h-5 text-[#00F0FF]" />}
                Servicio Activo
              </h3>
              <p className="text-sm text-[#9CA3AF]">Cliente: {activeItem.client_name}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded-lg text-sm">
                {getStatusLabel(activeItem.status, isTaxi ? 'taxi' : activeItemIsFood ? 'food' : 'delivery')}
              </span>
              <Button
                data-testid="active-chat-button"
                onClick={() => { setSelectedItem(activeItem); setShowChat(true); }}
                size="sm"
                className="bg-white/5 border border-white/10 text-white rounded-lg"
              >
                <MessageCircle className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <MapView
            pickup={activeItem.pickup_location}
            dropoff={activeItem.dropoff_location}
            vehiclePosition={isDelivery || isTaxi ? activeItem.provider_location : null}
            vehicleIcon={isDelivery ? 'moto' : 'car'}
            vehicleColor={isDelivery ? '#00F0FF' : '#FF007F'}
            height="300px"
          />

          <div className="flex flex-wrap gap-2 mt-4">
            {isTaxi && (
              <>
                <Button onClick={() => handleUpdateRideGps(activeItem.id)} className="bg-white/5 border border-white/10 text-white rounded-xl">
                  <Navigation className="w-4 h-4 mr-2" />
                  Actualizar GPS
                </Button>
                {activeItem.status === 'accepted' && buildPickupMapsUrl(activeItem.pickup_location) && (
                  <a href={buildPickupMapsUrl(activeItem.pickup_location)} target="_blank" rel="noreferrer">
                    <Button className="bg-white/5 border border-white/10 text-white rounded-xl">
                      <MapPin className="w-4 h-4 mr-2" />
                      Abrir ruta al cliente
                    </Button>
                  </a>
                )}
                {activeItem.status === 'in_progress' && buildGoogleMapsUrl(activeItem.pickup_location, activeItem.dropoff_location) && (
                  <a href={buildGoogleMapsUrl(activeItem.pickup_location, activeItem.dropoff_location)} target="_blank" rel="noreferrer">
                    <Button className="bg-white/5 border border-white/10 text-white rounded-xl">
                      <MapPin className="w-4 h-4 mr-2" />
                      Ruta al destino
                    </Button>
                  </a>
                )}
              </>
            )}
            {isDelivery && (
              <>
                <Button
                  onClick={() => handleUpdateGps(activeItem.id)}
                  className="bg-white/5 border border-white/10 text-white rounded-xl"
                >
                  <Navigation className="w-4 h-4 mr-2" />
                  Actualizar GPS
                </Button>
                {buildGoogleMapsUrl(activeItem.pickup_location, activeItem.dropoff_location) && (
                  <a
                    href={buildGoogleMapsUrl(activeItem.pickup_location, activeItem.dropoff_location)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button className="bg-white/5 border border-white/10 text-white rounded-xl">
                      <MapPin className="w-4 h-4 mr-2" />
                      Abrir ruta Google Maps
                    </Button>
                  </a>
                )}
              </>
            )}
            {isTaxi && activeItem.status === 'accepted' && (
              <Button
                data-testid="ride-arrived"
                onClick={() => handleRideAction(activeItem.id, 'arrived')}
                className="gradient-button rounded-xl"
              >
                He llegado
              </Button>
            )}
            {isTaxi && activeItem.status === 'arrived' && (
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <Label className="text-xs text-[#9CA3AF]">PIN del cliente</Label>
                  <Input
                    value={ridePins[activeItem.id] || ''}
                    onChange={(e) => setRidePins((current) => ({ ...current, [activeItem.id]: e.target.value }))}
                    placeholder="1234"
                    maxLength={4}
                    className="bg-white/5 border-white/10 text-white rounded-xl w-28"
                  />
                </div>
                <Button data-testid="ride-start" onClick={() => handleRideAction(activeItem.id, 'start')} className="gradient-button rounded-xl">
                  Iniciar viaje
                </Button>
              </div>
            )}
            {isTaxi && activeItem.status === 'in_progress' && (
              <Button
                data-testid="ride-complete"
                onClick={() => handleRideAction(activeItem.id, 'complete')}
                className="gradient-button rounded-xl"
              >
                Finalizar viaje
              </Button>
            )}
            {isTaxi && activeItem.payment_method === 'transfer' && activeItem.status === 'completed' && activeItem.transfer_status !== 'received' && (
              <Button onClick={() => handleConfirmTransfer(activeItem.id)} className="gradient-button rounded-xl">
                Confirmar pago recibido
              </Button>
            )}
            {isDelivery && ['accepted', 'delivery_assigned'].includes(activeItem.status) && (
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => e.target.files[0] && handleDeliveryStatus(activeItem.id, 'picked_up', e.target.files[0])}
                />
                <div className="gradient-button rounded-xl px-4 py-2 text-white text-sm">
                  📸 Foto de recolección
                </div>
              </label>
            )}
            {isDelivery && activeItem.status === 'picked_up' && (
              <Button onClick={() => handleDeliveryStatus(activeItem.id, activeItemIsFood ? 'on_the_way' : 'in_transit')} className="gradient-button rounded-xl">
                En camino
              </Button>
            )}
            {isDelivery && ['in_transit', 'on_the_way'].includes(activeItem.status) && (
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => e.target.files[0] && handleDeliveryStatus(activeItem.id, 'delivered', e.target.files[0])}
                />
                <div className="gradient-button rounded-xl px-4 py-2 text-white text-sm">
                  📸 Foto de entrega
                </div>
              </label>
            )}
          </div>
        </div>
      )}

      {/* Available items */}
      <div className="glass-card p-6 rounded-3xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-white flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            {isTaxi ? '🚕 Viajes Disponibles' : '📦 Pedidos Disponibles'}
          </h3>
          <Button onClick={loadData} size="sm" className="bg-white/5 border border-white/10 text-white rounded-lg">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {loading ? (
          <p className="text-center py-6 text-[#9CA3AF]">Cargando...</p>
        ) : availableItems.length === 0 ? (
          <p className="text-center py-6 text-[#9CA3AF]">
            No hay {isTaxi ? 'viajes' : 'pedidos'} disponibles en este momento
          </p>
        ) : (
          <div className="space-y-3">
            {availableItems.map((item) => (
              <div
                key={item.id}
                data-testid={`available-item-${item.id}`}
                className="bg-white/5 p-4 rounded-2xl border border-white/10 flex flex-wrap items-center justify-between gap-3"
              >
                <div className="flex-1 min-w-[200px]">
                  <p className="text-white font-medium">{item.client_name}</p>
                  <div className="flex items-center gap-1 text-xs text-[#9CA3AF] mt-1">
                    <MapPin className="w-3 h-3" />
                    <span className="truncate">{item.pickup_location?.address}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-[#9CA3AF]">
                    <Navigation className="w-3 h-3" />
                    <span className="truncate">{item.dropoff_location?.address}</span>
                  </div>
                </div>

                <div className="text-right">
                  {isTaxi ? (
                    <>
                      <p className="text-2xl font-semibold text-[#FF007F]" style={{ fontFamily: 'Outfit' }}>
                        ${Number(item.offered_price || 0).toFixed(2)}
                      </p>
                      <p className="text-xs text-[#9CA3AF]">
                        Sugerido ${Number(item.suggested_price || item.offered_price || 0).toFixed(2)} · ~{Number(item.estimated_distance || 0).toFixed(1)} km
                      </p>
                      <p className="text-xs text-[#D1D5DB] mt-1">
                        {item.payment_method === 'transfer'
                          ? 'Pago por transferencia'
                          : `Efectivo: ${item.cash_bill === 'exact' ? 'pago exacto' : `billete de $${item.cash_bill || 'exacto'}`}`}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-xl font-semibold text-[#00F0FF]" style={{ fontFamily: 'Outfit' }}>
                        {item.service_type}
                      </p>
                      <p className="text-xs text-[#9CA3AF]">
                        ${item.total_price?.toFixed(2)} · {item.items?.length || 0} items
                      </p>
                    </>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    data-testid={`accept-item-${item.id}`}
                    onClick={() => isTaxi ? handleQuickCounterOffer(item, 0) : handleAcceptDelivery(item.id)}
                    className="gradient-button rounded-xl"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    {isTaxi ? 'Aceptar precio' : 'Aceptar'}
                  </Button>
                  {isTaxi && (
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        min="0.5"
                        step="0.25"
                        placeholder="$"
                        value={counterOffers[item.id] || ''}
                        onChange={(e) => setCounterOffers((current) => ({ ...current, [item.id]: e.target.value }))}
                        className="bg-white/5 border-white/10 text-white rounded-xl w-24"
                      />
                      <Button onClick={() => handleCounterOffer(item.id)} className="bg-white/5 border border-white/10 text-white rounded-xl">
                        Enviar contraoferta
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Chat Dialog */}
      <Dialog open={showChat} onOpenChange={setShowChat}>
        <DialogContent className="bg-[#0B0C10] border-white/10 text-white max-w-lg rounded-3xl p-0">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>
              Chat con {selectedItem?.client_name}
            </DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="p-4">
              <ChatInterface
                orderId={selectedItem.id}
                orderType={isTaxi ? 'ride' : 'delivery'}
                otherPartyName={selectedItem.client_name}
                height="450px"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProviderServices;
